import {
  Box,
  CircularProgress,
  Divider,
  Flex,
  Icon,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Text,
  Link,
} from '@chakra-ui/react'
import { useContext, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { IoIosCheckmarkCircle } from 'react-icons/io'
import { MdCancel, MdPending } from 'react-icons/md'
import { MLContext } from '../../../providers'
import { OperationStatus } from '../../../interfaces'


interface StatusIndicatorProp {
  status: OperationStatus,
  errorMessage?: string
}
interface BulkJobStatusProp extends StatusIndicatorProp {
  name?: string
}

function statusIndicator({ status, errorMessage }: StatusIndicatorProp) {
  switch (status) {
    case 'success':
      return (
        <Icon
          as={IoIosCheckmarkCircle}
          color="appGreenishTeal"
          boxSize={6}
        />
      )
    case 'error':
      return (
        <Popover trigger="hover">
          <PopoverTrigger>
            <Box>
              <Icon
                as={MdCancel}
                color="appWatermelon"
                boxSize={6}
                cursor="pointer"
              />
            </Box>
          </PopoverTrigger>
          <PopoverContent>
            <PopoverArrow />
            <PopoverBody>{errorMessage}</PopoverBody>
          </PopoverContent>
        </Popover>
      )
    case 'pending':
      return (
        <Icon
          as={MdPending}
          color="appLightBlueGrey"
          boxSize={6}
        />
      )
    case 'indeterminate':
    default:
      return (
        <CircularProgress
          isIndeterminate
          color="appGreenishTeal"
          size={6}
        />
      )
  }
}
const BulkJobStatus = ({ name, status, errorMessage }: BulkJobStatusProp): React.ReactElement => {
  return <>
    <Divider />
    <Flex
      align="center"
      justify="space-between"
      padding="1em 1em"
    >
      <Box overflow="hidden" maxWidth="200px">
        <Text color="appDarkIndigo" isTruncated>
          {name}
        </Text>
      </Box>
      {statusIndicator({ status: status, errorMessage: errorMessage })}
    </Flex>
  </>

}

const ActiveOperationStatus = (): React.ReactElement => {
  const { t } = useTranslation()
  const [{ copyItems, moveItems, renameFolderItem }] = useContext(MLContext)
  const copyCount = copyItems.length
  const moveCount = moveItems.length
  const renameCount = renameFolderItem.length
  const [isOpen, setIsOpen] = useState(false)
  const onOpen = (): void => {
    setIsOpen((prev) => !prev)
  }
  const onClose = (): void => {
    setIsOpen(false)
  }

  const totalCount: number = copyCount + moveCount + renameCount
  useEffect(() => {
    setIsOpen(true)
  }, [[...copyItems, ...moveItems, ...renameFolderItem].map((item) => item?.jobId ?? item.id).filter(Boolean).join(",")])

  if (totalCount > 0) {
    return (
      <Popover closeOnBlur={true} returnFocusOnClose={true} placement="top-end" isOpen={isOpen} >
        <PopoverTrigger>
          <Link
            color="appDarkIndigo"
            fontSize="12px"
            padding="0 10px"
            _hover={{
              textDecoration: 'none',
              backgroundColor: 'appPaleGreyTwo',
            }}
            onClick={onOpen}
          >
            {t('active_operation', 'Active operation')}{' '}
          </Link>
        </PopoverTrigger>{' '}
        <Portal >
          <PopoverContent onBlur={onClose}>
            <PopoverArrow />
            <PopoverCloseButton onClick={onClose} />
            <PopoverHeader>
              <Trans t={t} i18nKey="copy_move_actions">
                <Text fontWeight="medium">{{ totalCount }} actions</Text>
              </Trans>
            </PopoverHeader>
            <PopoverBody p="0">
              <Box overflowY="auto" maxHeight="500px">
                {copyItems.length > 0 && (
                  <>
                    <Box padding="0.5em 1em" backgroundColor="appPaleGrey">
                      <Trans t={t} i18nKey="copy_actions">
                        <Text color="appDarkIndigo" fontWeight="normal">
                          Copying {{ copyCount }} item(s)
                        </Text>
                      </Trans>
                    </Box>
                    {copyItems.map(item => (
                      <BulkJobStatus
                        key={item.id}
                        name={item.name}
                        status={item.status}
                        errorMessage={item.errorMessage} />
                    ))}
                  </>
                )}
                {moveItems.length > 0 && (
                  <>
                    <Box padding="0.5em 1em" backgroundColor="appPaleGrey">
                      <Trans t={t} i18nKey="move_actions">
                        <Text color="appDarkIndigo" fontWeight="normal">
                          Moving {{ moveCount }} item(s)
                        </Text>
                      </Trans>
                    </Box>
                    {moveItems.map(item => (
                      <BulkJobStatus
                        key={item.id}
                        name={item.name}
                        status={item.status}
                        errorMessage={item.errorMessage} />
                    ))}
                  </>
                )}
                {renameFolderItem.length > 0 && (
                  <>
                    <Box padding="0.5em 1em" backgroundColor="appPaleGrey">
                      <Trans t={t} i18nKey="rename_action">
                        <Text color="appDarkIndigo" fontWeight="normal">
                          Renaming folder
                        </Text>
                      </Trans>
                    </Box>
                    {renameFolderItem.map(item => (
                      <BulkJobStatus
                        key={item.id}
                        name={item.name}
                        status={item.status}
                        errorMessage={item.errorMessage} />
                    ))}
                  </>
                )}
              </Box>
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    )
  }
  return <></>
}

export default ActiveOperationStatus
